/**
 * Client Key Manager
 *
 * Centralized singleton for managing the client_key.
 * This ensures we never hardcode 'athleto' and always fetch from the global user.
 */

import { getAuth } from 'firebase/auth';
import { getDoc } from 'firebase/firestore';
import { GlobalUserRef_Document } from '../refs/globalUsers';

class ClientKeyManager {
  private static instance: ClientKeyManager;
  private cachedClientKey: string | null = null;
  private fetchPromise: Promise<string> | null = null;

  private constructor() {}

  static getInstance(): ClientKeyManager {
    if (!ClientKeyManager.instance) {
      ClientKeyManager.instance = new ClientKeyManager();
    }
    return ClientKeyManager.instance;
  }

  /**
   * Get the client key for the current authenticated user
   * Uses caching to avoid repeated Firestore reads
   */
  async getClientKey(): Promise<string> {
    // Return cached value if available
    if (this.cachedClientKey) {
      return this.cachedClientKey;
    }

    // If a fetch is already in progress, wait for it
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Start a new fetch
    this.fetchPromise = this.fetchClientKeyFromAuth();

    try {
      const clientKey = await this.fetchPromise;
      this.cachedClientKey = clientKey;
      return clientKey;
    } finally {
      this.fetchPromise = null;
    }
  }

  private async fetchClientKeyFromAuth(): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('User not authenticated. Cannot fetch client key.');
    }

    try {
      const globalUserRef = GlobalUserRef_Document(user.uid);
      const globalUserSnap = await getDoc(globalUserRef);

      if (!globalUserSnap.exists()) {
        throw new Error(`Global user document not found for user ${user.uid}`);
      }

      const globalUserData = globalUserSnap.data();
      const clientKey = globalUserData?.client_key;

      if (!clientKey || clientKey === '') {
        throw new Error('Client key not set in global user document');
      }

      return clientKey;
    } catch (error: any) {      throw new Error(`Failed to fetch client key: ${error.message}`);
    }
  }

  /**
   * Set the client key (called by UserContext when it loads)
   * This allows us to avoid Firestore reads in most cases
   */
  setClientKey(clientKey: string): void {
    this.cachedClientKey = clientKey;
  }

  /**
   * Clear the cached client key (e.g., on logout)
   */
  clearClientKey(): void {
    this.cachedClientKey = null;
    this.fetchPromise = null;
  }

  /**
   * Get cached client key synchronously (returns null if not cached)
   */
  getCachedClientKey(): string | null {
    return this.cachedClientKey;
  }
}

// Export singleton instance
export const clientKeyManager = ClientKeyManager.getInstance();

/**
 * Helper function to get client key
 * If clientKey is provided, returns it immediately
 * Otherwise, fetches from the global user document
 */
export const ensureClientKey = async (providedClientKey?: string): Promise<string> => {
  if (providedClientKey) {
    return providedClientKey;
  }
  return clientKeyManager.getClientKey();
};
